﻿import React, { useCallback, useEffect, useState } from 'react'
import { Heading, Text, Button, Input, LoadingSpinner, Editor } from '../../components/ui'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { PermissionGuard } from '../../components/PermissionGuard'
import { qualityService } from '../../services/quality'
import { modulesService, userGroupsService } from '../../services/modules'
import { QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET, QUALITY_PARAM_SLA_GRUPO_PRIMEIRA_RESPOSTA } from '../../services/qualityTicketNotifications'
import { QualityCategory, QualityImprocedentReason, QualityParameter, QualityTicketOrigin, QualityTicketOrigin as QualityIncidentOrigin, QualityTicketStage as QualityIncidentStage, QualityTicketType as QualityIncidentType } from '../../types/quality'
import { UserGroup } from '../../types/modules'
import { QUALITY_PARAM_DEPARTAMENTO } from './qualidadeKind'
import { parseSlaPrimeiraRespostaGroupIdsFromValue } from './qualityBusinessHours'

const MODULE_NAME = 'qualidade_cadastro'
/** Suprime o texto padrão "Carregando permissões..." — cada aba já exibe loading próprio dos dados. */
const CADASTRO_PERM_LOADING: React.ReactNode = null

type Tab = 'categories' | 'stages' | 'incident_types' | 'improcedent_reasons' | 'origins' | 'parameters'

/** Gera identificador snake_case único (ex.: `name` de categoria, `key` de etapa). */
function slugifyUniqueIdentifier(raw: string, taken: Set<string>, emptyFallback: string, maxLen = 80): string {
  let base = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, maxLen)
  if (!base) base = emptyFallback
  let candidate = base
  let n = 2
  while (taken.has(candidate)) {
    candidate = `${base}_${n}`
    n += 1
  }
  return candidate
}

function slugifyStageKey(name: string, taken: Set<string>): string {
  return slugifyUniqueIdentifier(name.trim(), taken, 'etapa', 60)
}

const QualidadeCadastro: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('categories')
  const [moduleTitle, setModuleTitle] = useState<string>('Qualidade • Cadastros')
  const [moduleDescription, setModuleDescription] = useState<string>('Cadastros básicos do módulo de qualidade')

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then(result => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const tabButtonClass = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          loadingComponent={CADASTRO_PERM_LOADING}
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {moduleDescription}
              </Text>
            </div>

            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'categories')}
                  onClick={() => setActiveTab('categories')}
                >
                  Categorias
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'origins')}
                  onClick={() => setActiveTab('origins')}
                >
                  Onde
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'incident_types')}
                  onClick={() => setActiveTab('incident_types')}
                >
                  Motivo
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'improcedent_reasons')}
                  onClick={() => setActiveTab('improcedent_reasons')}
                >
                  Registo improcedente
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'stages')}
                  onClick={() => setActiveTab('stages')}
                >
                  Etapas (Kanban)
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'parameters')}
                  onClick={() => setActiveTab('parameters')}
                >
                  Parâmetros
                </button>
              </nav>
            </div>

            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'stages' && <StagesTab />}
            {activeTab === 'incident_types' && <IncidentTypesTab />}
            {activeTab === 'improcedent_reasons' && <ImprocedentReasonsTab />}
            {activeTab === 'origins' && <OriginsTab />}
            {activeTab === 'parameters' && <ParametersTab />}
          </div>
        </PermissionGuard>
      </main>
  )
}

// ---- Aba Categorias ----
const CategoriesTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<QualityCategory[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityCategory> | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await qualityService.listCategories()
    setCategories(data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({ display_name: '', description: '', is_active: true })
    setIsModalOpen(true)
  }

  const openEdit = (cat: QualityCategory) => {
    setEditing({ ...cat })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const save = async () => {
    if (!editing?.display_name?.trim()) return
    setSaving(true)
    try {
      const payload = { ...editing }
      delete (payload as any).deleter
      if (!payload.id) {
        const taken = new Set(categories.map(c => c.name))
        payload.name = slugifyUniqueIdentifier(payload.display_name!.trim(), taken, 'categoria', 100)
      }
      await qualityService.upsertCategory(payload)
      closeModal()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir esta categoria? A exclusão é lógica: deixa de aparecer na lista principal e nas seleções, ' +
      'mas tickets já vinculados mantêm a referência. Ficam registrados o usuário e a data da exclusão.'
    )) return
    await qualityService.deleteCategory(id)
    await load()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Text size="sm" color="muted">
          {categories.length} categoria(s) ativa(s)
        </Text>
        <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
          <Button variant="solid" onClick={openNew}>Nova categoria</Button>
        </PermissionGuard>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exibição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ativo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center"><LoadingSpinner /></td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma categoria cadastrada.
                </td>
              </tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{cat.display_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{cat.description || '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      cat.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {cat.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Editar" onClick={() => openEdit(cat)} className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Excluir" onClick={() => remove(cat.id)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                {editing.id ? 'Editar categoria' : 'Nova categoria'}
              </Text>
              <Button variant="ghost" onClick={closeModal}>Fechar</Button>
            </div>

            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome de exibição *</Text>
                <Input
                  placeholder="ex: Processo, Equipamento"
                  value={editing.display_name || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, display_name: e.target.value }))}
                />
                <Text size="xs" color="muted" className="mt-1">
                  {editing.id
                    ? 'O identificador interno (slug) não é alterado ao editar o nome de exibição.'
                    : 'O identificador interno (slug) será gerado automaticamente a partir do nome de exibição.'}
                </Text>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Descrição</Text>
                <Input
                  placeholder="Descrição opcional"
                  value={editing.description || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, description: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="cat-active"
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing(prev => ({ ...prev!, is_active: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="cat-active" className="text-sm text-gray-700 dark:text-gray-300">Ativo</label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <Button
                variant="solid"
                onClick={save}
                disabled={saving || !editing.display_name?.trim()}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ---- Aba Etapas ----
const DEFAULT_COLOR = '#6b7280'

const StagesTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stages, setStages] = useState<QualityIncidentStage[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityIncidentStage> | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await qualityService.listStages()
    setStages(data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({ name: '', order_index: (stages.length + 1) * 10, is_closed: false, color: null })
    setIsModalOpen(true)
  }

  const openEdit = (stage: QualityIncidentStage) => {
    setEditing({ ...stage })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const save = async () => {
    if (!editing?.name?.trim()) return
    setSaving(true)
    try {
      const payload = { ...editing }
      delete (payload as any).deleter
      if (!payload.id) {
        const taken = new Set(stages.map(s => s.key))
        payload.key = slugifyStageKey(payload.name!.trim(), taken)
      }
      await qualityService.upsertStage(payload)
      closeModal()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir esta etapa? A exclusão é lógica: deixa de aparecer no Kanban e na lista de etapas ativas, ' +
      'mas tickets já vinculados mantêm a referência. Ficam registrados o usuário e a data da exclusão.'
    )) return
    try {
      await qualityService.deleteStage(id)
      await load()
    } catch {
      alert('Não foi possível excluir esta etapa.')
    }
  }

  // Cor de exibição: cor definida ou cinza padrão
  const displayColor = (stage: QualityIncidentStage) => stage.color || DEFAULT_COLOR

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Text size="sm" color="muted">
          {stages.length} etapa(s) ativa(s)
        </Text>
        <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
          <Button variant="solid" onClick={openNew}>Nova etapa</Button>
        </PermissionGuard>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cor</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ordem</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Encerrado</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center"><LoadingSpinner /></td>
              </tr>
            ) : stages.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma etapa cadastrada.
                </td>
              </tr>
            ) : (
              stages.map((stage) => (
                <tr key={stage.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2">
                    <span
                      className="inline-block w-6 h-6 rounded-full border border-gray-200 dark:border-gray-600"
                      style={{ backgroundColor: displayColor(stage) }}
                      title={stage.color || 'Cor padrão'}
                    />
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{stage.name}</td>
                  <td className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">{stage.order_index}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      stage.is_closed
                        ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    }`}>
                      {stage.is_closed ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Editar" onClick={() => openEdit(stage)} className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Excluir" onClick={() => remove(stage.id)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                {editing.id ? 'Editar etapa' : 'Nova etapa'}
              </Text>
              <Button variant="ghost" onClick={closeModal}>Fechar</Button>
            </div>

            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome de exibição *</Text>
                <Input
                  placeholder="ex: Em revisão"
                  value={editing.name || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, name: e.target.value }))}
                />
                <Text size="xs" color="muted" className="mt-1">
                  {editing.id
                    ? 'A chave interna não é alterada ao editar o nome.'
                    : 'A chave interna será gerada automaticamente a partir do nome.'}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Ordem</Text>
                <input
                  type="number"
                  min={0}
                  placeholder="Ex: 10"
                  value={editing.order_index ?? ''}
                  onChange={(e) => setEditing(prev => ({
                    ...prev!,
                    order_index: e.target.value !== '' ? Number(e.target.value) : 0,
                  }))}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Seletor de cor */}
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">Cor da etapa</Text>
                <div className="flex items-center gap-3">
                  {/* Paleta nativa */}
                  <input
                    type="color"
                    value={editing.color || DEFAULT_COLOR}
                    onChange={(e) => setEditing(prev => ({ ...prev!, color: e.target.value }))}
                    className="h-10 w-14 cursor-pointer rounded border border-gray-300 dark:border-gray-600 p-0.5 bg-white dark:bg-gray-800"
                    title="Selecionar cor"
                  />
                  {/* Campo hex manual */}
                  <input
                    type="text"
                    maxLength={7}
                    placeholder="#6b7280"
                    value={editing.color || ''}
                    onChange={(e) => {
                      const val = e.target.value
                      if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        setEditing(prev => ({ ...prev!, color: val || null }))
                      }
                    }}
                    className="w-32 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white font-mono placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {/* Preview */}
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: editing.color || DEFAULT_COLOR }}
                  >
                    {editing.name || 'Prévia'}
                  </span>
                  {editing.color && (
                    <button
                      type="button"
                      onClick={() => setEditing(prev => ({ ...prev!, color: null }))}
                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 underline"
                    >
                      Limpar
                    </button>
                  )}
                </div>
                <Text size="xs" color="muted" className="mt-1">Cor usada no Kanban. Deixe em branco para usar a cor padrão do sistema.</Text>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="stage-closed"
                  type="checkbox"
                  checked={editing.is_closed ?? false}
                  onChange={(e) => setEditing(prev => ({ ...prev!, is_closed: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="stage-closed" className="text-sm text-gray-700 dark:text-gray-300">
                  Etapa de encerramento
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <Button
                variant="solid"
                onClick={save}
                disabled={saving || !editing.name?.trim()}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ---- Aba Motivo ----
const IncidentTypesTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<QualityIncidentType[]>([])
  const [allOrigins, setAllOrigins] = useState<QualityTicketOrigin[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityIncidentType> & { origin_ids?: string[] } | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [data, origins] = await Promise.all([
      qualityService.listTicketTypesWithOrigins(),
      qualityService.listTicketOriginsActive(),
    ])
    setItems(data)
    setAllOrigins(origins)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({ display_name: '', description: '', is_active: true, origin_ids: [] })
    setIsModalOpen(true)
  }

  const openEdit = (item: QualityIncidentType) => {
    setEditing({ ...item, origin_ids: item.origin_ids ?? [] })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const toggleOrigin = (originId: string) => {
    setEditing(prev => {
      if (!prev) return prev
      const current = prev.origin_ids ?? []
      const next = current.includes(originId)
        ? current.filter(id => id !== originId)
        : [...current, originId]
      return { ...prev, origin_ids: next }
    })
  }

  const save = async () => {
    if (!editing?.display_name?.trim()) return
    if (!editing.origin_ids || editing.origin_ids.length === 0) {
      alert('Selecione pelo menos uma origem para este tipo.')
      return
    }
    setSaving(true)
    try {
      const payload = { ...editing }
      delete (payload as any).deleter
      if (!payload.id) {
        const taken = new Set(items.map(i => i.name))
        payload.name = slugifyUniqueIdentifier(payload.display_name!.trim(), taken, 'tipo', 100)
      }
      await qualityService.upsertTicketType(payload)
      closeModal()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir este tipo? A exclusão é lógica: deixa de aparecer na lista principal, ' +
      'mas tickets já vinculados mantêm a referência.'
    )) return
    await qualityService.deleteIncidentType(id)
    await load()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Text size="sm" color="muted">
          {items.length} motivo(s) ativo(s)
        </Text>
        <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
          <Button variant="solid" onClick={openNew}>Novo motivo</Button>
        </PermissionGuard>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exibição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Onde</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ativo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center"><LoadingSpinner /></td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhum tipo cadastrado.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const linkedOrigins = allOrigins.filter(o => item.origin_ids?.includes(o.id))
                return (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.display_name}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                      {linkedOrigins.length === 0
                        ? <span className="text-gray-400 dark:text-gray-500">—</span>
                        : linkedOrigins.map(o => (
                          <span key={o.id} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs">
                            {o.display_name}
                          </span>
                        ))
                      }
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{item.description || '—'}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        item.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                          <button type="button" title="Editar" onClick={() => openEdit(item)} className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                          <button type="button" title="Excluir" onClick={() => remove(item.id)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                {editing.id ? 'Editar tipo' : 'Novo motivo'}
              </Text>
              <Button variant="ghost" onClick={closeModal}>Fechar</Button>
            </div>

            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome de exibição *</Text>
                <Input
                  placeholder="ex: Reclamação, Não conformidade"
                  value={editing.display_name || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, display_name: e.target.value }))}
                />
                <Text size="xs" color="muted" className="mt-1">
                  {editing.id
                    ? 'O identificador interno (slug) não é alterado ao editar o nome de exibição.'
                    : 'O identificador interno (slug) será gerado automaticamente a partir do nome de exibição.'}
                </Text>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Descrição</Text>
                <Input
                  placeholder="Descrição opcional"
                  value={editing.description || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, description: e.target.value }))}
                />
              </div>

              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                  Onde *
                  <span className="ml-1 text-xs font-normal text-gray-400">(selecione ao menos uma)</span>
                </Text>
                {allOrigins.length === 0 ? (
                  <Text size="xs" color="muted">Nenhuma origem ativa cadastrada.</Text>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {allOrigins.map(origin => {
                      const checked = editing.origin_ids?.includes(origin.id) ?? false
                      return (
                        <label
                          key={origin.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer select-none text-sm transition-colors
                            border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60
                            has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:dark:bg-primary-900/20"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOrigin(origin.id)}
                            className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded"
                          />
                          <span className={checked ? 'text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'}>
                            {origin.display_name}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="type-active"
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing(prev => ({ ...prev!, is_active: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="type-active" className="text-sm text-gray-700 dark:text-gray-300">Ativo</label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <Button
                variant="solid"
                onClick={save}
                disabled={saving || !editing.display_name?.trim() || !editing.origin_ids?.length}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ---- Aba Registo improcedente ----
const ImprocedentReasonsTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<QualityImprocedentReason[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityImprocedentReason> | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await qualityService.listImprocedentReasons()
    setItems(data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({ display_name: '', description: '', is_active: true })
    setIsModalOpen(true)
  }

  const openEdit = (item: QualityImprocedentReason) => {
    setEditing({ ...item })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const save = async () => {
    if (!editing?.display_name?.trim()) return
    setSaving(true)
    try {
      const payload = { ...editing }
      delete (payload as { deleter?: unknown }).deleter
      if (!payload.id) {
        const taken = new Set(items.map(i => i.name))
        payload.name = slugifyUniqueIdentifier(payload.display_name!.trim(), taken, 'improcedente', 100)
      }
      await qualityService.upsertImprocedentReason(payload)
      closeModal()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir este registo improcedente? A exclusão é lógica: deixa de aparecer na lista principal, ' +
      'mas tickets já vinculados mantêm a referência.'
    )) return
    await qualityService.deleteImprocedentReason(id)
    await load()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Text size="sm" color="muted">
          {items.length} tipo(s) de registo improcedente ativo(s)
        </Text>
        <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
          <Button variant="solid" onClick={openNew}>Novo registo improcedente</Button>
        </PermissionGuard>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exibição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ativo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center"><LoadingSpinner /></td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhum registo improcedente cadastrado.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.display_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{item.description || '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      item.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {item.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Editar" onClick={() => openEdit(item)} className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Excluir" onClick={() => remove(item.id)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                {editing.id ? 'Editar registo improcedente' : 'Novo registo improcedente'}
              </Text>
              <Button variant="ghost" onClick={closeModal}>Fechar</Button>
            </div>

            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome de exibição *</Text>
                <Input
                  placeholder="ex: Fora do escopo, Duplicado"
                  value={editing.display_name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditing(prev => ({ ...prev!, display_name: e.target.value }))}
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Descrição</Text>
                <Input
                  placeholder="Descrição opcional"
                  value={editing.description || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditing(prev => ({ ...prev!, description: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="improcedent-active"
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing(prev => ({ ...prev!, is_active: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="improcedent-active" className="text-sm text-gray-700 dark:text-gray-300">Ativo</label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <Button
                variant="solid"
                onClick={save}
                disabled={saving || !editing.display_name?.trim()}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ---- Aba Origem do Ticket / Melhoria ----
const OriginsTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<QualityIncidentOrigin[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<QualityIncidentOrigin> | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await qualityService.listIncidentOrigins()
    setItems(data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({ display_name: '', description: '', is_active: true })
    setIsModalOpen(true)
  }

  const openEdit = (item: QualityIncidentOrigin) => {
    setEditing({ ...item })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditing(null)
  }

  const save = async () => {
    if (!editing?.display_name?.trim()) return
    setSaving(true)
    try {
      const payload = { ...editing }
      delete (payload as any).deleter
      if (!payload.id) {
        const taken = new Set(items.map(i => i.name))
        payload.name = slugifyUniqueIdentifier(payload.display_name!.trim(), taken, 'origem', 100)
      }
      await qualityService.upsertIncidentOrigin(payload)
      closeModal()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm(
      'Excluir esta origem? A exclusão é lógica: deixa de aparecer na lista principal, ' +
      'mas tickets já vinculados mantêm a referência.'
    )) return
    await qualityService.deleteIncidentOrigin(id)
    await load()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Text size="sm" color="muted">
          {items.length} origem(ns) ativa(s)
        </Text>
        <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
          <Button variant="solid" onClick={openNew}>Nova origem</Button>
        </PermissionGuard>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/30">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exibição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ativo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center"><LoadingSpinner /></td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma origem cadastrada.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.display_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{item.description || '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      item.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {item.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Editar" onClick={() => openEdit(item)} className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <button type="button" title="Excluir" onClick={() => remove(item.id)} className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                {editing.id ? 'Editar origem' : 'Nova origem'}
              </Text>
              <Button variant="ghost" onClick={closeModal}>Fechar</Button>
            </div>

            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome de exibição *</Text>
                <Input
                  placeholder="ex: Auditoria interna, Cliente externo"
                  value={editing.display_name || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, display_name: e.target.value }))}
                />
                <Text size="xs" color="muted" className="mt-1">
                  {editing.id
                    ? 'O identificador interno (slug) não é alterado ao editar o nome de exibição.'
                    : 'O identificador interno (slug) será gerado automaticamente a partir do nome de exibição.'}
                </Text>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Descrição</Text>
                <Input
                  placeholder="Descrição opcional"
                  value={editing.description || ''}
                  onChange={(e: any) => setEditing(prev => ({ ...prev!, description: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="origin-active"
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing(prev => ({ ...prev!, is_active: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="origin-active" className="text-sm text-gray-700 dark:text-gray-300">Ativo</label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <Button
                variant="solid"
                onClick={save}
                disabled={saving || !editing.display_name?.trim()}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ---- Aba Parâmetros ----
const ParametersTab: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [parameters, setParameters] = useState<QualityParameter[]>([])
  const [departments, setDepartments] = useState<{ id: string; name: string; display_name: string }[]>([])
  const [userGroups, setUserGroups] = useState<UserGroup[]>([])
  const [editingParameter, setEditingParameter] = useState<QualityParameter | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [editMode, setEditMode] = useState<'simple' | 'html'>('simple')
  const [saving, setSaving] = useState(false)

  const selectClass =
    'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [data, deps, groupsResult] = await Promise.all([
        qualityService.listQualityParameters(),
        qualityService.listDepartmentsActive(),
        userGroupsService.getAll(),
      ])
      setParameters(data)
      setDepartments(deps)
      setUserGroups(groupsResult.data || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const isDepartamentoQualidadeParam = editingParameter?.key === QUALITY_PARAM_DEPARTAMENTO
  const isUserGroupsMultiParam =
    editingParameter?.key === QUALITY_PARAM_SLA_GRUPO_PRIMEIRA_RESPOSTA ||
    editingParameter?.key === QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET
  const allowEmptyValue = isDepartamentoQualidadeParam || isUserGroupsMultiParam
  const slaSelectedGroupIds = isUserGroupsMultiParam
    ? parseSlaPrimeiraRespostaGroupIdsFromValue(editValue)
    : []
  const slaGroupOptions = userGroups.filter(g => g.is_active || slaSelectedGroupIds.includes(g.id))
  const userGroupsEmptyLabel =
    editingParameter?.key === QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET
      ? 'Nenhum (não envia)'
      : 'Nenhum (qualquer comentário)'

  const toggleSlaGroup = (id: string) => {
    const next = slaSelectedGroupIds.includes(id)
      ? slaSelectedGroupIds.filter(x => x !== id)
      : [...slaSelectedGroupIds, id]
    setEditValue(next.join(','))
  }

  const openEdit = (param: QualityParameter) => {
    setEditingParameter(param)
    setEditValue(param.value)
    setEditMode(typeof param.value === 'string' && param.value.includes('<') ? 'html' : 'simple')
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingParameter(null)
    setEditMode('simple')
  }

  const save = async () => {
    if (!editingParameter) return
    if (!allowEmptyValue && !editValue.trim()) return
    setSaving(true)
    try {
      await qualityService.updateQualityParameter(editingParameter.id, { value: editValue.trim() })
      await load()
      closeModal()
    } catch (err: any) {
      alert(err?.message || 'Erro ao salvar parâmetro. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="mb-4">
        <Text size="sm" color="muted">
          Parâmetros de configuração do módulo de qualidade. Edite o valor conforme necessário.
        </Text>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Chave</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Descrição</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Valor</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {parameters.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nenhum parâmetro cadastrado.
                  </td>
                </tr>
              ) : (
                parameters.map(param => (
                  <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-sm font-mono text-gray-700 dark:text-gray-300">
                      {param.key}
                    </td>
                    <td
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate"
                      title={param.description ?? undefined}
                    >
                      {param.description ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {(() => {
                        if (
                          param.key === QUALITY_PARAM_SLA_GRUPO_PRIMEIRA_RESPOSTA ||
                          param.key === QUALITY_PARAM_GRUPO_NOTIFICACAO_TICKET
                        ) {
                          const ids = parseSlaPrimeiraRespostaGroupIdsFromValue(param.value)
                          if (ids.length === 0) return '—'
                          const labels = ids.map(id => userGroups.find(g => g.id === id)?.display_name ?? id)
                          const text = labels.join(', ')
                          return text.length > 40 ? `${text.slice(0, 40)}…` : text
                        }
                        const raw = String(param.value)
                        return raw.length > 40 ? `${raw.slice(0, 40)}…` : raw
                      })()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                        <Button variant="outline" size="sm" onClick={() => openEdit(param)}>
                          Editar
                        </Button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de edição */}
      {isModalOpen && editingParameter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Editar parâmetro
              </Heading>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <Text size="sm" className="font-mono text-gray-500 dark:text-gray-400 mb-4 block">
              {editingParameter.key}
            </Text>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300">
                    {allowEmptyValue ? 'Valor' : 'Valor *'}
                  </Text>
                  {!isDepartamentoQualidadeParam && !isUserGroupsMultiParam && (
                    <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 overflow-hidden text-xs">
                      <button
                        type="button"
                        className={`px-2 py-1 ${editMode === 'simple' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                        onClick={() => setEditMode('simple')}
                      >
                        Valor simples
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${editMode === 'html' ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
                        onClick={() => setEditMode('html')}
                      >
                        Descritivo HTML
                      </button>
                    </div>
                  )}
                </div>

                {isDepartamentoQualidadeParam ? (
                  <select
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">— Nenhum (desabilita Não aplicável) —</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.display_name}</option>
                    ))}
                  </select>
                ) : isUserGroupsMultiParam ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Grupos de usuário</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {slaSelectedGroupIds.length === 0
                          ? userGroupsEmptyLabel
                          : `${slaSelectedGroupIds.length} selecionado${slaSelectedGroupIds.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {slaGroupOptions.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Nenhum grupo cadastrado.</div>
                      ) : (
                        <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700">
                          {slaGroupOptions.map(g => (
                            <li key={g.id} className="px-3 py-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={slaSelectedGroupIds.includes(g.id)}
                                  onChange={() => toggleSlaGroup(g.id)}
                                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                                />
                                <span className="truncate">{g.display_name}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : editMode === 'simple' ? (
                  <Input
                    type="text"
                    value={editValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <Editor
                    value={editValue}
                    onChange={v => setEditValue(v)}
                    placeholder="Digite o conteúdo em HTML"
                    stickyToolbar
                  />
                )}
              </div>

              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1 block">Descrição</Text>
                <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                  {editingParameter.description ?? '—'}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancelar</Button>
              <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CADASTRO_PERM_LOADING}>
                <Button
                  variant="solid"
                  onClick={save}
                  disabled={saving || (!allowEmptyValue && !editValue.trim())}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default QualidadeCadastro
